/* Assortiment section - Top 10 best-selling products grid
 * Updated: Redesigned to match the premium full-image card style used on HaagplantenPage.
 *          Cards now feature full-image backgrounds with alternating corner radii, dark gradient
 *          overlay, price badge top-right, product name at bottom in white, and a quick-add
 *          cart button. Scroll reveals use opacity only so cards never move vertically.
 * Updated: Fetch minimum_order_quantity and pass it to addItem so min-order products (e.g. 25)
 *          are added with the correct quantity instead of defaulting to 1. */

import { motion } from 'framer-motion';
import { ShoppingCart, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';

import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

interface Product {
  id: string;
  name: string;
  scientific_name: string;
  description: string;
  image_url: string | null;
  active: boolean;
  category_name: string;
  category_slug: string;
  base_slug: string;
  variant_count: number;
  min_price: number;
  total_stock: number;
  sort: string | null;
  is_simple_product: boolean;
  single_variant_slug?: string;
  featured: boolean;
  btw_percentage: number;
  default_variant_id?: string;
  default_variant_size?: string;
  default_variant_type?: string;
  default_variant_price?: number;
  default_variant_min_quantity?: number;
  simple_price?: number;
}

const Assortiment = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const { addItem } = useCart();

  useEffect(() => {
    fetchTopProducts();
  }, []);

  const fetchTopProducts = async () => {
    try {
      setLoading(true);

      const { data: fallbackData, error: fallbackError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          scientific_name,
          description,
          image_url,
          active,
          category_id,
          base_slug,
          btw_percentage,
          sort,
          is_simple_product,
          simple_price,
          featured,
          product_categories!inner (
            name,
            slug
          )
        `)
        .eq('active', true)
        .order('featured', { ascending: false })
        .order('name', { ascending: true })
        .limit(10);

      if (fallbackError) throw fallbackError;

      const eligibleProducts = (fallbackData || []).filter((product: any) => {
        const categorySlug = product.product_categories.slug;
        return categorySlug !== 'aanplant-en-verzorging' && categorySlug !== 'tuinaccessoires';
      });

      const nonSimpleIds = eligibleProducts.filter((p: any) => !p.is_simple_product).map((p: any) => p.id);
      const { data: variantsBatch } = nonSimpleIds.length
        ? await supabase
            .from('product_variants')
            .select('product_id, id, size, type, price_tier_1, stock_quantity, slug, active, minimum_order_quantity')
            .in('product_id', nonSimpleIds)
            .eq('active', true)
        : { data: [] as any[] };

      const variantsByProduct = new Map<string, any[]>();
      (variantsBatch || []).forEach((v: any) => {
        if (!variantsByProduct.has(v.product_id)) variantsByProduct.set(v.product_id, []);
        variantsByProduct.get(v.product_id)!.push(v);
      });

      const formattedData = eligibleProducts.map((product: any) => {
          const btwPercentage = product.btw_percentage || 9;

          if (product.is_simple_product) {
            const minPrice = calculatePriceInclBTW(product.simple_price || 0, btwPercentage);

            return {
              id: product.id,
              name: product.name,
              scientific_name: product.scientific_name,
              description: product.description,
              image_url: product.image_url,
              active: product.active,
              category_name: product.product_categories.name,
              category_slug: product.product_categories.slug,
              base_slug: product.base_slug,
              variant_count: 0,
              min_price: minPrice,
              total_stock: 999,
              sort: product.sort || null,
              is_simple_product: true,
              single_variant_slug: undefined,
              featured: product.featured || false,
              btw_percentage: btwPercentage,
              simple_price: product.simple_price || 0,
            };
          }

          const variants = variantsByProduct.get(product.id) || [];

          const variantCount = variants.length;
          const singleVariantSlug = variantCount === 1 ? variants[0].slug : undefined;
          const minPriceExclBTW = variants.length
            ? Math.min(...variants.map((v: any) => parseFloat(v.price_tier_1)))
            : 0;
          const minPrice = calculatePriceInclBTW(minPriceExclBTW, btwPercentage);
          const totalStock = variants.reduce(
            (sum: number, v: any) => sum + (v.stock_quantity || 0),
            0
          );

          const defaultVariant = variants.length ? variants[0] : undefined;

          return {
            id: product.id,
            name: product.name,
            scientific_name: product.scientific_name,
            description: product.description,
            image_url: product.image_url,
            active: product.active,
            category_name: product.product_categories.name,
            category_slug: product.product_categories.slug,
            base_slug: product.base_slug,
            variant_count: variantCount,
            min_price: minPrice,
            total_stock: totalStock,
            sort: product.sort || null,
            is_simple_product: false,
            single_variant_slug: singleVariantSlug,
            featured: product.featured || false,
            btw_percentage: btwPercentage,
            default_variant_id: defaultVariant?.id,
            default_variant_size: defaultVariant?.size,
            default_variant_type: defaultVariant?.type,
            default_variant_price: defaultVariant ? parseFloat(defaultVariant.price_tier_1) : undefined,
            default_variant_min_quantity: defaultVariant?.minimum_order_quantity || 1,
          };
        });

      const productIds = formattedData.map(p => p.id);
      if (productIds.length > 0) {
        const { data: primaryImages } = await supabase
          .from('product_images')
          .select('product_id, storage_path, uses_new_bucket')
          .in('product_id', productIds)
          .is('variant_id', null)
          .eq('is_primary', true);

        if (primaryImages && primaryImages.length > 0) {
          const imageMap = new Map(primaryImages.map(img => [
            img.product_id,
            getProductImageUrl(img.storage_path, img.uses_new_bucket)
          ]));
          for (const product of formattedData) {
            if (imageMap.has(product.id)) {
              product.image_url = imageMap.get(product.id) || product.image_url;
            }
          }
        }
      }

      setProducts(formattedData.slice(0, 10));
    } catch (error) {
      console.error('Error fetching top products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAddToCart = async (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (product.is_simple_product) {
      setAddingToCart(product.id);
      try {
        const priceInclBTW = calculatePriceInclBTW(product.simple_price || 0, product.btw_percentage);
        await addItem({
          product_id: product.id,
          product_name: product.name,
          variant_size: '',
          unit_price: priceInclBTW,
          slug: product.base_slug,
          image_url: product.image_url || '/assets/preview.webp',
          is_simple_product: true,
          btw_percentage: product.btw_percentage,
        });
      } catch {
        toast.error('Kon product niet toevoegen aan winkelwagen');
      } finally {
        setAddingToCart(null);
      }
      return;
    }

    if (!product.default_variant_id) {
      toast.error('Geen variant beschikbaar voor dit product');
      return;
    }

    setAddingToCart(product.id);
    try {
      const priceInclBTW = calculatePriceInclBTW(
        product.default_variant_price || 0,
        product.btw_percentage
      );
      await addItem({
        product_variant_id: product.default_variant_id,
        product_name: product.name,
        variant_size: product.default_variant_size || '',
        variant_type: product.default_variant_type as 'Kluit' | 'Pot' | 'Kale wortel',
        unit_price: priceInclBTW,
        slug: product.base_slug,
        image_url: product.image_url || '/assets/preview.webp',
        btw_percentage: product.btw_percentage,
        quantity: product.default_variant_min_quantity || 1,
      });
    } catch {
      toast.error('Kon product niet toevoegen aan winkelwagen');
    } finally {
      setAddingToCart(null);
    }
  };

  return (
    <section id="haagplanten" data-fast-scroll="viewed_top_products" className="py-20 bg-[#fcfaf6] relative z-10 w-full">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex items-end justify-between gap-6 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5a6552] mb-3">Direct vanuit onze Dedemsvaartse kwekerij</p>
            <h2 className="text-4xl md:text-5xl text-[#1e2b22]">Onze meest verkochte producten</h2>
          </div>
          <a href="/haagplanten" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#1e2b22] hover:text-[#5a6552] transition-colors">
            Bekijk alle producten <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>

        {loading && (
          <div className="mt-8 text-center">
            <p className="text-gray-500">Laden...</p>
          </div>
        )}

        {/* Product Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mt-8"
        >
          <div className={`grid gap-3 sm:gap-4 lg:gap-5 ${products.length === 1 ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-5'}`}>
            {products.map((product, index) => (
              <motion.a
                key={product.id}
                href={
                  product.is_simple_product
                    ? `/${product.base_slug}`
                    : product.variant_count === 1 && product.single_variant_slug
                      ? `/${product.single_variant_slug}`
                      : `/${product.base_slug}`
                }
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="group relative block overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                style={{
                  borderTopLeftRadius: index % 2 === 0 ? '2.5rem' : 0,
                  borderBottomRightRadius: index % 2 === 0 ? '2.5rem' : 0,
                  borderTopRightRadius: index % 2 === 0 ? 0 : '2.5rem',
                  borderBottomLeftRadius: index % 2 === 0 ? 0 : '2.5rem',
                  height: '300px',
                }}
              >
                {/* Full background image */}
                <img
                  src={product.image_url || '/assets/preview.webp'}
                  alt={product.name}
                  width={400}
                  height={320}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />

                {/* Bottom-to-top gradient overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0) 65%)',
                  }}
                />

                {/* Price badge top-right */}
                {product.min_price > 0 && (
                  <div
                    className="absolute top-3 right-3 text-white text-xs font-bold px-2.5 py-1.5"
                    style={{
                      backgroundColor: '#1e2b22',
                      borderTopRightRadius: '10px',
                      borderBottomLeftRadius: '10px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3), 0 0 12px rgba(255,255,255,0.7)'
                    }}
                  >
                    {product.is_simple_product || product.variant_count <= 1 ? '' : 'Vanaf '}€{product.min_price.toFixed(2)}
                  </div>
                )}

                {/* Bottom content: name + cart button */}
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 py-4 gap-2">
                  <div className="flex-1 min-w-0">
                    {product.scientific_name && (
                      <p className="text-xs text-white/70 italic mb-0.5 line-clamp-1">
                        {product.scientific_name}
                      </p>
                    )}
                    <span
                      className="text-sm font-bold text-white leading-tight line-clamp-2 transition-all duration-200 group-hover:underline group-hover:decoration-[#5a6552] group-hover:decoration-2 [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]"
                    >
                      {product.name}
                    </span>
                  </div>

                  {/* Quick add to cart */}
                  <motion.button
                    onClick={(e) => handleQuickAddToCart(product, e)}
                    disabled={addingToCart === product.id}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-[#5a6552] hover:bg-[#1e2b22] disabled:opacity-50 transition-colors shadow-lg"
                    style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}
                    aria-label={`${product.name} toevoegen aan winkelwagen`}
                  >
                    <ShoppingCart className="w-4 h-4 text-white" />
                  </motion.button>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Assortiment;
